import { Button, Loading } from '../../../components';
import { useEffect, useState } from 'react';
import { FinancialType, ProcessMitigation } from './PAMitigation';
import {
  UPDATE_MITIGATION_PROCESS,
  UPDATE_MITIGATION_PROCESS_AREA,
} from '../queries/processMitigation';
import { useMutation } from '@apollo/client';
import { SubmitHandler, useForm } from 'react-hook-form';
import { message } from 'antd';

interface FormData {
  externalDelay: number | string;
  externalPercentage: number | string;
  internalDelay: number | string;
  internalPercentage: number | string;
  onsiteDelay: number | string;
  onsitePercentage: number | string;
  strategyComment: string;
}

interface UpdateMitigationProps {
  readonly item: ProcessMitigation;
  readonly financialType: FinancialType;
  readonly locationId: string;
  readonly processId: string;
  readonly mitigationType: string;
  readonly onClose: () => void;
}

export function UpdateMitigation({
  item,
  financialType,
  locationId,
  processId,
  mitigationType,
  onClose,
}: UpdateMitigationProps) {
  const [divisionUUID, setDivisionUUID] = useState<string>('');
  const [intermediateUUID, setIntermediateUUID] = useState<string>('');
  const [productFinancialUUID, setProductFinancialUUID] = useState<string>('');
  const [productGroupUUID, setProductGroupUUID] = useState<string>('');

  const mutation =
    mitigationType === 'process-area-mitigation'
      ? UPDATE_MITIGATION_PROCESS_AREA
      : UPDATE_MITIGATION_PROCESS;

  const [UpdateMitigation, { loading, error }] = useMutation(mutation, {
    refetchQueries: [
      'GetMitigationQuantificationPerProcessArea',
      'GetMitigationQuantificationPerProcess',
    ],
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  useEffect(() => {
    switch (financialType) {
      case 'division':
        setDivisionUUID(item.key);
        setProductFinancialUUID('');
        setProductGroupUUID('');
        break;
      case 'intermediate':
        setIntermediateUUID(item.key);
        setProductFinancialUUID('');
        setProductGroupUUID('');
        break;
      case 'product':
        setProductFinancialUUID(item.key);
        setProductGroupUUID('');
        setDivisionUUID('');
        break;
      case 'productGroup':
        setProductGroupUUID(item.key);
        setProductFinancialUUID('');
        setDivisionUUID('');
        break;
    }
  }, [item]);

  const onFinish: SubmitHandler<FormData> = async (values) => {
    const parseValue = (value: number | string) => parseFloat(value.toString());

    const externalDelay = parseValue(values.externalDelay);
    const externalPercentage = parseValue(values.externalPercentage);
    const internalDelay = parseValue(values.internalDelay);
    const internalPercentage = parseValue(values.internalPercentage);
    const onsiteDelay = parseValue(values.onsiteDelay);
    const onsitePercentage = parseValue(values.onsitePercentage);

    const sharedData = {
      divisionUUID,
      intermediateUUID,
      productFinancialUUID,
      productGroupUUID,
      externalDelay,
      externalPercentage,
      internalDelay,
      internalPercentage,
      onsiteDelay,
      onsitePercentage,
      strategyComment: values.strategyComment,
      locationUUID: locationId,
    };

    const mitigationData =
      mitigationType === 'process-area-mitigation'
        ? {
            ...sharedData,
            processAreaMitigationUUID: '',
            processAreaUUID: processId,
          }
        : { ...sharedData, processMitigationUUID: '', processUUID: processId };

    try {
      const response = await UpdateMitigation({
        variables: { data: mitigationData },
      });

      if (response.data) {
        message.success('Changes Saved');
        onClose();
      }
    } catch (e) {
      console.error(e);
      message.error(e instanceof Error ? e.message : 'Operation Failed');
    }
  };

  useEffect(() => {
    const defaultValues = {
      externalDelay: Math.round(item.mitigation.externalDelay) || '',
      externalPercentage: Math.round(item.mitigation.externalPercentage) || '',
      internalDelay: Math.round(item.mitigation.internalDelay) || '',
      internalPercentage: Math.round(item.mitigation.internalPercentage) || '',
      onsiteDelay: Math.round(item.mitigation.onsiteDelay) || '',
      onsitePercentage: Math.round(item.mitigation.onsitePercentage) || '',
      strategyComment: item.mitigation.strategyComment,
    };
    reset(defaultValues);
  }, [item, reset]);

  if (loading) return <Loading />;

  if (error) {
    message.error('Failed to update mitigation');
    console.error(error);
  }

  return (
    <form
      className="  w-full flex flex-col flex-nowrap mt-2"
      onSubmit={handleSubmit(onFinish)}
    >
      <div className="w-full relative my-4">
        <span className="  font-semibold text-primary-100 text-[14px] absolute right-56">
          Mitigation
        </span>
        <span className="font-semibold text-primary-100 text-[14px] absolute right-12">
          Ramp-up
        </span>
      </div>
      <div className="inline-flex flex-row flex-nowrap items-center mt-2 mb-0.5 justify-between">
        <label
          className="font-semibold text-primary-100 text-[14px]"
          htmlFor="onsitePercentage"
        >
          Onsite Mitigation
        </label>
        <div className="flex">
          <div>
            <span
              className={`mx-4 w-28 border border-[#CCCCCC] rounded-md text-right text-primary-100 font-normal text-sm px-2 py-1 max-h-7 flex-1 ${
                errors?.['onsitePercentage']
                  ? 'border-danger focus:border-danger bg-danger/5'
                  : ''
              }`}
            >
              <input
                placeholder="0"
                className="w-24 bg-transparent border-none text-right text-primary-100 font-normal text-[14px] focus:outline-none focus:ring-0"
                type="text"
                step={0.01}
                {...register('onsitePercentage', {
                  min: {
                    value: 0,
                    message: 'Percentage must be greater than 0.',
                  },
                  max: {
                    value: 100,
                    message: 'Percentage must be less than 100.',
                  },
                  pattern: { value: /^[0-9]*$/, message: 'Invalid number' },
                })}
              />
              %
            </span>
            {errors?.['onsitePercentage'] && (
              <span className="block max-w-[112px] ml-auto text-right text-danger text-sm font-normal  ">
                {errors['onsitePercentage'].message}
              </span>
            )}
          </div>
          <div>
            <span
              className={`mx-4 w-28 border border-[#CCCCCC] rounded-md text-right text-primary-100 font-normal text-sm px-2 py-1 max-h-7 flex-1 ${
                errors?.['onsiteDelay']
                  ? 'border-danger focus:border-danger bg-danger/5'
                  : ''
              }`}
            >
              <input
                placeholder="0"
                className="w-24 bg-transparent border-none text-right text-primary-100 font-normal text-[14px] focus:outline-none focus:ring-0"
                type="text"
                step={0.01}
                {...register('onsiteDelay', {
                  min: {
                    value: 0,
                    message: 'Delay must be greater than 0.',
                  },
                  pattern: { value: /^[0-9]*$/, message: 'Invalid number' },
                })}
              />
              mths
            </span>
            {errors?.['onsiteDelay'] && (
              <span className="block max-w-[112px] ml-auto text-right text-danger text-sm font-normal  ">
                {errors['onsiteDelay'].message}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="inline-flex flex-row flex-nowrap items-center mb-0.5 justify-between">
        <label className="form-label" htmlFor="internalPercentage">
          Internal Mitigation
        </label>
        <div className="flex">
          <div>
            <span
              className={`mx-4 w-28 border border-[#CCCCCC] rounded-md text-right text-primary-100 font-normal text-sm px-2 py-1 max-h-7 flex-1 ${
                errors?.['internalPercentage']
                  ? 'border-danger focus:border-danger bg-danger/5'
                  : ''
              }`}
            >
              <input
                placeholder="0"
                className="w-24 bg-transparent border-none text-right text-primary-100 font-normal text-[14px] focus:outline-none focus:ring-0"
                type="text"
                step={0.01}
                {...register('internalPercentage', {
                  min: {
                    value: 0,
                    message: 'Percentage must be greater than 0.',
                  },
                  max: {
                    value: 100,
                    message: 'Percentage must be less than 100.',
                  },
                  pattern: { value: /^[0-9]*$/, message: 'Invalid number' },
                })}
              />
              %
            </span>
            {errors?.['internalPercentage'] && (
              <span className="block max-w-[112px] ml-auto text-right text-danger text-sm font-normal  ">
                {errors['internalPercentage'].message}
              </span>
            )}
          </div>
          <div>
            <span
              className={`mx-4 w-28 border border-[#CCCCCC] rounded-md text-right text-primary-100 font-normal text-sm px-2 py-1 max-h-7 flex-1 ${
                errors?.['internalDelay']
                  ? 'border-danger focus:border-danger bg-danger/5'
                  : ''
              }`}
            >
              <input
                placeholder="0"
                className="w-24 bg-transparent border-none text-right text-primary-100 font-normal text-[14px] focus:outline-none focus:ring-0"
                type="text"
                step={0.01}
                {...register('internalDelay', {
                  min: {
                    value: 0,
                    message: 'Delay must be greater than 0.',
                  },
                  pattern: { value: /^[0-9]*$/, message: 'Invalid number' },
                })}
              />
              mths
            </span>
            {errors?.['internalDelay'] && (
              <span className="block max-w-[112px] ml-auto text-right text-danger text-sm font-normal  ">
                {errors['internalDelay'].message}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="inline-flex flex-row flex-nowrap items-center mb-0.5 justify-between">
        <label className="form-label" htmlFor="externalPercentage">
          External Mitigation
        </label>
        <div className="flex">
          <div>
            <span
              className={`mx-4 w-28 border border-[#CCCCCC] rounded-md text-right text-primary-100 font-normal text-sm px-2 py-1 max-h-7 flex-1 ${
                errors?.['externalPercentage']
                  ? 'border-danger focus:border-danger bg-danger/5'
                  : ''
              }`}
            >
              <input
                placeholder="0"
                className="w-24 bg-transparent border-none text-right text-primary-100 font-normal text-[14px] focus:outline-none focus:ring-0"
                type="text"
                step={0.01}
                {...register('externalPercentage', {
                  min: {
                    value: 0,
                    message: 'Percentage must be greater than 0.',
                  },
                  max: {
                    value: 100,
                    message: 'Percentage must be less than 100.',
                  },
                  pattern: { value: /^[0-9]*$/, message: 'Invalid number' },
                })}
              />
              %
            </span>
            {errors?.['externalPercentage'] && (
              <span className="block max-w-[112px] ml-auto text-right text-danger text-sm font-normal  ">
                {errors['externalPercentage'].message}
              </span>
            )}
          </div>
          <div>
            <span
              className={`mx-4 w-28 border border-[#CCCCCC] rounded-md text-right text-primary-100 font-normal text-sm px-2 py-1 max-h-7 flex-1 ${
                errors?.['externalDelay']
                  ? 'border-danger focus:border-danger bg-danger/5'
                  : ''
              }`}
            >
              <input
                placeholder="0"
                className="w-24 bg-transparent border-none text-right text-primary-100 font-normal text-[14px] focus:outline-none focus:ring-0"
                type="text"
                step={0.01}
                {...register('externalDelay', {
                  min: {
                    value: 0,
                    message: 'Delay must be greater than 0.',
                  },
                  pattern: { value: /^[0-9]*$/, message: 'Invalid number' },
                })}
              />
              mths
            </span>
            {errors?.['externalDelay'] && (
              <span className="block max-w-[112px] ml-auto text-right text-danger text-sm font-normal  ">
                {errors['externalDelay'].message}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="inline-flex flex-col flex-nowrap items-start my-2 justify-between">
        <label className="form-label mb-2" htmlFor="strategyComment">
          Mitigation Strategy Commentary
        </label>
        <textarea
          placeholder="Add comment (optional)"
          rows={5}
          className={`w-full border border-secondary-100 rounded-lg text-left text-primary-100 font-normal text-sm px-2 py-1 
               ${
                 errors?.strategyComment &&
                 'border-danger focus:border-danger bg-danger/5'
               }
              `}
          {...register('strategyComment')}
        />
      </div>
      <div className="inline-flex flex-col items-center mt-6">
        <Button
          htmlType={'submit'}
          type="primary"
          className="min-w-[188px] mx-auto h-7"
        >
          Save
        </Button>
      </div>
    </form>
  );
}
